'use client';

import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import createCache from '@emotion/cache';
import { ThemeProvider, CacheProvider } from '@emotion/react';
import { enableStaticRendering } from 'mobx-react';
import getTheme from '@/Theme/theme';
import '../../Theme/Styles/Bootstrap.scss';
import '../../Theme/Styles/IBE.scss';
import { boot, initTranslations, productSearchSessionKey } from '@/Config/config';
import { routeChangeEvent } from '@ibe/services';
import {
  GlobalTranslationFunction,
  GlobalTranslationContextProvider,
  IsNextEnvContext,
  IsOnProductPageProvider,
  useAuth,
  useConfig,
  useAppService
} from '@ibe/components';
import isClient, { logger, stepSessionStorage } from '@/Util/globals';
import GlobalWindowProvider from '@/Util/GlobalWindowProvider';
import { ClientSideBootingFinishedContext } from '@/Util/useClientSideBootingFinished';
import ErrorBoundary from '@/components/ErrorBoundary';
import { SearchPropertiesProvider } from '@/Util/useSearchProperties';
import { GlobalMGLPropsContext } from '@/Util/GlobalMGLPropsContext';
import { PageProps } from '@/Util/magnolia';
import { IsBookingInitializedProvider } from '@/Util/useIsBookingInitialized';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import initTrackingSubscriptions from '@/Tracking/trackingSubscriptions';
import CheckoutStore from '@/components/checkout/CheckoutStore';
import { useMount } from 'react-use';
import { ApiAffiliateRequest, ApiAffiliateRequestFromJSON, ApiAffiliateResponse } from '@ibe/api';
import ApplyBranding from '@/Util/ApplyBranding';
import { useTranslation } from '@/app/i18n/client';
import Klaro from '@/components/contextProviders/Klaro';
import SessionTimeOutModal from '../session/SessionTimeOutModal';

const cache = createCache({ key: 'next' });

export type ApplicationProps = {
  isProduction: boolean;
  locale: string;
  isLocal?: string;
  pageProps: PageProps;
};

const ContextProvider = ({
  children,
  pageProps,
  isProduction,
  isLocal,
  locale
}: PropsWithChildren<ApplicationProps>) => {
  enableStaticRendering(!isClient());

  const { translationVersion, magnoliaHost, magnoliaHostClient } = pageProps ?? {};
  const serverSideBootingFinished = useRef<boolean>(false);
  const [clientSideBootingFinished, setClientSideBootingFinished] = useState<boolean>(false);
  const auth = useAuth();
  const appService = useAppService();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const config = useConfig();
  const [sessionTimeOutNotificationOffset] = useState(
    config.session.expirationTimeNotificationOffset
  );
  const [sessionExpirationTimeOut, setSessionExpirationTimeOut] = useState(
    auth.authService.getRemainingTokenTime()
  );
  const [cssVars, setCssVars] = useState<{ [key: string]: string } | undefined>(undefined);
  const [faviconUrl, setFaviconUrl] = useState<string | undefined>(undefined);

  (async (): Promise<void> => {
    if (!isClient()) {
      await initTranslations(isLocal ?? '', magnoliaHost, locale, translationVersion);
    }
  })();

  useMount(() => {
    if (!clientSideBootingFinished && isClient()) {
      (async () => {
        const { magnoliaContext } = (pageProps ?? {}) as PageProps;

        const config = await boot(
          !isProduction,
          (isClient() ? magnoliaHostClient : magnoliaHost) ?? '',
          pageProps,
          locale,
          isLocal,
          translationVersion
        );
        if (
          !magnoliaContext?.isMagnoliaEdit &&
          !magnoliaContext?.isMagnolia &&
          pageProps?.config?.useConsentManager
        ) {
          initTrackingSubscriptions(config);
          logger('log')('tracking initialized...');
        }
        if (config.affiliateAgencyNumber !== undefined) {
          let affResponse: ApiAffiliateResponse | undefined;
          try {
            affResponse = await appService.api.getAffiliateData(
              ApiAffiliateRequestFromJSON({
                number: `${config.affiliateAgencyNumber || 0}`,
                apiKey: ''
              } as ApiAffiliateRequest)
            );
          } catch (err) {
            console.error('Unable to load affiliate data:', err);
          }
          if (affResponse) {
            setCssVars(affResponse.cssVars);
            if (affResponse.metaContent?.content?.favicon) {
              setFaviconUrl(affResponse.metaContent?.content?.favicon as unknown as string);
            }
          }
        }
        setClientSideBootingFinished(true);
      })();
    }
  });

  useEffect(() => {
    setSessionExpirationTimeOut(auth.authService.getRemainingTokenTime());
  }, [auth.authService, auth.authState.token, pathname]);

  useEffect(() => {
    routeChangeEvent.broadcast({
      pathname: document.location.pathname,
      search: document.location.search
    });
  }, [JSON.stringify(searchParams)]);

  const logout = () => {
    auth.removeToken();
    auth.authService.invalidate();
    sessionStorage.removeItem(config.sessionKeyCart);
    sessionStorage.removeItem(config.sessionKeySearchParams);
    sessionStorage.removeItem(productSearchSessionKey);
    sessionStorage.removeItem(CheckoutStore.PACKAGE_CART_ID);
    stepSessionStorage.clear();
    router.push('/');
  };

  return serverSideBootingFinished.current || clientSideBootingFinished ? (
    <div id="iso">
      {!!cssVars && <ApplyBranding cssVars={cssVars} faviconUrl={faviconUrl} />}
      <CacheProvider value={cache}>
        <GlobalWindowProvider>
          <GlobalTranslationContextProvider
            globalTranslation={useTranslation as unknown as GlobalTranslationFunction}
          >
            <IsNextEnvContext.Provider value={true}>
              <GlobalMGLPropsContext.Provider value={pageProps}>
                <ClientSideBootingFinishedContext.Provider value={clientSideBootingFinished}>
                  <IsOnProductPageProvider>
                    <IsBookingInitializedProvider>
                      <SearchPropertiesProvider>
                        <ThemeProvider theme={getTheme()}>
                          <ErrorBoundary router={router}>
                            {children}
                            {auth.authState.token && sessionExpirationTimeOut !== null && (
                              <SessionTimeOutModal
                                sessionTimeOut={sessionExpirationTimeOut}
                                sessionTimeOutNotificationOffset={
                                  sessionTimeOutNotificationOffset * 60 * 1000
                                }
                                onLogoutRedirection={logout}
                                silentRefreshThrottle={10_000}
                              />
                            )}
                            <Klaro />
                          </ErrorBoundary>
                        </ThemeProvider>
                      </SearchPropertiesProvider>
                    </IsBookingInitializedProvider>
                  </IsOnProductPageProvider>
                </ClientSideBootingFinishedContext.Provider>
              </GlobalMGLPropsContext.Provider>
            </IsNextEnvContext.Provider>
          </GlobalTranslationContextProvider>
        </GlobalWindowProvider>
      </CacheProvider>
    </div>
  ) : (
    <></>
  );
};

export default ContextProvider;
